import React, { useState, useEffect, useCallback } from 'react';
import './SalesPage.css';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { getFilteredNavItemsWithIcons } from '../../config/routes';
import HasPermi from '../../components/HasPermi';
import { ConfigProvider, Button, Tabs, Table, Tag, Pagination } from 'antd';
import { todayYmd, addDaysYmd, formatDateTime, APP_TIMEZONE } from '../../utils/timezone';


// function RefId({ value }) {
//   const [copied, setCopied] = useState(false);
//   if (!value) return <span className="ref-id-short">—</span>;
//   const handleCopy = () => {
//     navigator.clipboard.writeText(value);
//     setCopied(true);
//     setTimeout(() => setCopied(false), 1500);
//   };
//   return (
//     <div className="ref-id-wrapper" onClick={handleCopy}>
//       <span className="ref-id-short">{value.slice(0, 8)}...</span>
//       <span className="ref-id-tooltip">{copied ? 'Copied!' : value}</span>
//     </div>
//   );
// }

const ORDER_STATUS = { 0: 'Ongoing', 1: 'Completed', 2: 'Cancelled' };
const RESERVATION_STATUS = { 0: 'Pending', 1: 'Confirmed', 2: 'Cancelled' };
const RES_STATUS_COLOR = { 0: 'gold', 1: 'green', 2: 'red', 4: 'blue' };

const VOID_ACTION_COLOR = { 1: 'Void Food', 2: 'Void Table' };

// Transaction action codes returned by the API
const TRANSACTION_ACTION = {
  1: 'Open Table',
  2: 'New Order(Dine In)',
  3: 'New Order(Takeaway)',
  4: 'Cancelled order',
  5: 'Close Bill',
  6: 'Void Item',
  7: 'Add Items',
  8: 'Edit Items',
  9: 'Transfer Table',
  10: 'Add Item(Dine In)',
  11: 'Add Item(Takeaway)',
};

const TRANSACTION_ACTION_COLOR = {
  1: 'blue',    // Open Table
  2: 'purple',   // New Order(Dine In)
  3: 'purple',  // New Order(Takeaway)
  4: 'red',     // Cancelled order
  5: 'gold',    // Close Bill
  6: 'red',     // Void Item
  7: 'green',   // Add Items
  8: 'orange',  // Edit Items
  9: 'cyan',    // Transfer Table
  10: 'green',   // ADD Item(Dine In)
  11: 'green', // ADD  Item(Takeaway)
};



/**
 *  Catering 也属于Take away    、Reserve属于dine in，
 *  0 - 堂食Dine-in，1 - 预订Reserve，2 - 外卖Take away，3 - 餐饮服务
*/
const DINING_MODE_MAP = {
  0: { label: 'Dine-in', color: 'blue' },
  1: { label: 'Dine-in', color: 'blue' },
  2: { label: 'Take away', color: 'purple' },
  3: { label: 'Take away', color: 'purple' },
};

const PAGE_SIZE = 10;

// 标签选项主题

const theme = {
  components: {
    Tabs: {
      cardBg: '#ffffff',
      cardGutter: 10,
      itemColor: '#000000',
      itemHoverColor: '#000000',
      itemSelectedColor: '#ffffff',
      itemActiveColor: '#ffffff',
      inkBarColor: '#000000',
    }
  }
};

const TAB_ITEMS = [
  { key: 'salesItem', label: 'Sales Item' },
  { key: 'salesReport', label: 'Sales Report' },
  { key: 'transaction', label: 'Transaction' },
  { key: 'reservation', label: 'Reservation' },
  { key: 'voidReport', label: 'Void Report' },
  { key: 'endDay', label: 'End Day' },
];

const today = todayYmd();
const yesterday = addDaysYmd(-1);
const thirtyDaysAgo = addDaysYmd(-30);

// ── Column definitions ──────────────────────────────────────────────────────

const salesItemCols = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  { title: 'Product', dataIndex: 'product', key: 'product', render: v => v || '—' },
  { title: 'SKU', dataIndex: 'reference', key: 'reference', render: v => v || '—' },
  { title: 'Qty', dataIndex: 'qty', key: 'qty', align: 'center' },
  {
    title: 'Mode (Takeaway / Dine In)', dataIndex: 'mode', key: 'mode',
    render: v => {
      const mode = DINING_MODE_MAP[v];
      return mode ? <Tag color={mode.color}>{mode.label}</Tag> : <Tag>Default</Tag>;
    },
  },
  { title: 'Remark', dataIndex: 'remark', key: 'remark', className: 'sales-items-cell', render: v => v || '—' },
];
const salesReportCols = [
  { title: 'ID', dataIndex: 'itemIndex', key: 'itemIndex', width: 40 },
  { title: 'Receipt No', dataIndex: 'orderNumber', key: 'orderNumber', width: 170, render: v => v || '—' },
  { title: 'Customer', dataIndex: 'customer', key: 'customer', width: 100, render: v => v || '—' },
  { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', width: 100, render: v => formatDateTime(v) },
  { title: 'No of Item', dataIndex: 'itemCount', key: 'itemCount', width: 70, align: 'center', render: (v, r) => v ?? (r.items?.length ?? '—') },
  { title: 'Subtotal', dataIndex: 'subtotal', key: 'subtotal', width: 60, align: 'right', render: v => fmt(v ?? 0) },
  { title: 'Discount', dataIndex: 'discountAmount', key: 'discountAmount', width: 60, align: 'right', render: v => fmt(v ?? 0) },
  { title: 'GST', dataIndex: 'gst', key: 'gst', width: 60, align: 'right', render: (v, r) => fmt(v ?? r.tax ?? 0) },
  { title: 'Total', dataIndex: 'amount', key: 'amount', width: 60, align: 'right', render: fmt, sorter: (a, b) => a.amount - b.amount },
  { title: 'Remark', dataIndex: 'remark', key: 'remark', width: 190, className: 'sales-items-cell', render: v => v || '—' },
];

// Main row: each order with its current status
const transactionCols = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  { title: 'Receipt No', dataIndex: 'orderNumber', key: 'orderNumber', render: v => v || '—' },
  {
    title: 'Table No / Queue No', dataIndex: 'tableNo', key: 'tableNo', width: 160, render: (v, r) => {
      if (v != null) return v;
      const mode = DINING_MODE_MAP[r.diningOption];
      return mode ? mode.label : '—';
    }
  },
  {
    title: 'Product', dataIndex: 'orderItem', key: 'product', render: items => {
      if (!items || items.length === 0) return '—';
      return items.map(it => it.itemName || '—').join(', ');
    }
  },
  { title: 'Time', dataIndex: 'createdAt', key: 'createdAt', render: v => formatDateTime(v) },
  {
    title: 'Action', dataIndex: 'orderStatus', key: 'orderStatus', render: v => {
      const label = ORDER_STATUS[v] || '—';
      const color = { Ongoing: 'gold', Completed: 'green', Cancelled: 'red' }[label] || 'default';
      return <Tag color={color}>{label}</Tag>;
    }
  },
];

// 展开的 — 操作记录
const transactionSubCols = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  {
    title: 'Product', dataIndex: 'itemName', key: 'itemName', render: (v, r) => {
      const qty = r.qty;
      return v ? (qty ? `${v} x ${qty}` : v) : '—';
    }
  },
  { title: 'Time', dataIndex: 'createdAt', key: 'createdAt', render: v => formatDateTime(v) },
  {
    title: 'Action', dataIndex: 'action', key: 'action', render: v => {
      const label = TRANSACTION_ACTION[v] || '—';
      const color = TRANSACTION_ACTION_COLOR[v] || 'default';
      return <Tag color={color}>{label}</Tag>;
    }
  },
   { title: 'operatorName', dataIndex: 'operatorName', key: 'operatorName' },
   { title: 'Remark', dataIndex: 'remark', key: 'remark' },
  
];

const reservationCols = [
  { title: 'ID', dataIndex: 'index', key: 'index', width: 60 },
  { title: 'Reservation ID', dataIndex: 'reference', key: 'reference', render: v => v || '—' },
  { title: 'Customer', dataIndex: 'customer', key: 'customer', render: v => v || '—' },
  {
    title: 'Reservation Time', dataIndex: 'reservationTime', key: 'reservationTime', render: (v, r) => {
      const t = v || (r.date && r.time ? `${r.date} ${r.time}` : r.date);
      return formatDateTime(t);
    }
  },
  {
    title: 'Status', dataIndex: 'status', key: 'status', render: v => {
      const s = parseInt(v, 10);
      return <Tag color={RES_STATUS_COLOR[s]}>{RESERVATION_STATUS[s] ?? '—'}</Tag>;
    }
  },
  { title: 'Checkin Time', dataIndex: 'checkinTime', key: 'checkinTime', render: v => v ? formatDateTime(v) : '—' },
  { title: 'Assigned Table', dataIndex: 'assignedTable', key: 'assignedTable', render: (v, r) => v || r.tableNo || '—' },
  { title: 'Subtotal', dataIndex: 'subtotal', key: 'subtotal', render: v => fmt(v ?? 0) },
  { title: 'Discount', dataIndex: 'discountAmount', key: 'discountAmount', render: v => fmt(v ?? 0) },
  { title: 'GST', dataIndex: 'gst', key: 'gst', render: (v, r) => fmt(v ?? r.tax ?? 0) },
  { title: 'Total', dataIndex: 'total', key: 'total', render: (v, r) => fmt(v ?? r.amount ?? 0) },

];

const voidCols = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  {
    title: 'Date Time', dataIndex: 'createdAt', key: 'createdAt', render: (v, r) => {
      const t = v || (r.voidDate && r.voidTime ? `${r.voidDate} ${r.voidTime}` : r.voidDate);
      return formatDateTime(t);
    }
  },
  {
    title: 'Action', dataIndex: 'action', key: 'action', render: v => {
      const s = parseInt(v, 10);
      return <Tag color={RES_STATUS_COLOR[s]}>{VOID_ACTION_COLOR[s] ?? '—'}</Tag>;

    }
  },
  { title: 'Reference', dataIndex: 'reference', key: 'reference', render: v => v || '—' },
  { title: 'Remark', dataIndex: 'reason', key: 'reason', className: 'sales-items-cell', render: (v, r) => v || r.details || '—' },
];

const TABLE_CONFIG = {
  salesItem: { title: 'Sales Item Report', columns: salesItemCols },
  salesReport: { title: 'Sales Report', columns: salesReportCols },
  transaction: { title: 'Transaction Report', columns: transactionCols },
  reservation: { title: 'Reservation Report', columns: reservationCols },
  voidReport: { title: 'Void Report', columns: voidCols },
  endDay: { title: 'End Day Report', columns: voidCols },
};

// ── 工具函数（组件外，不依赖 state） ─────────────────────────────────────────

function fmt(v) {
  // console.log("fmt==========>", v);
  if (v === null || v === undefined || v === '' || isNaN(v)) { return '$0.00' };

  return `$${Number(v).toFixed(2)}`;
}

function SalesPage({ onNavigate }) {
  const { selectedRestaurant } = useRestaurant() || {};

  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [activeTable, setActiveTable] = useState('salesItem');


  // ── 每个 Tab 独立的状态（合并分页、loading、数据） ──────────────────────────
  const [tabs, setTabs] = useState({});
  const [summary, setSummary] = useState({ totalSales: 0, totalTransactions: 0, averageOrder: 0 });
  const [endDayReportData, setEndDayReportData] = useState(null);
  const [endDayDate, setEndDayDate] = useState(yesterday);

  const currentTab = tabs[activeTable] || { items: [], page: 1, total: 0, loading: false, rawData: null };
  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  // ── 请求接口 ──────────────────────────────────────────────────────────────
  // tabKey: tab 标识, page: 页码
  const fetchTab = useCallback(async (tabKey, page = 1) => {
    const ref = selectedRestaurant?.reference;
    if (!ref) return;

    setTabs(prev => ({
      ...prev,
      [tabKey]: { items: [], page: 1, total: 0, rawData: null, ...(prev[tabKey] || {}), loading: true },
    }));

    console.log("=======", ref, "startDate==>", startDate, "endDate===>", endDate, "page", page, "PAGE_SIZE==>", PAGE_SIZE);

    try {
      const base = `?tenantReference=${ref}&startDate=${startDate}&endDate=${endDate}&page=${page}&pageSize=${PAGE_SIZE}`;
      let url;
      switch (tabKey) {
        case 'salesItem':
          url = buildApiUrl(`/sales/salesItem${base}&type=1`);
          break;
        case 'salesReport':
          url = buildApiUrl(`/sales/newReport${base}&type=1`);
          break;
        case 'transaction':
          url = buildApiUrl(`/transaction/getInfo${base}&type=1`);
          break;
        case 'reservation':
          url = buildApiUrl(`/sales/reservation${base}&type=1`);
          break;
        case 'voidReport':
          url = buildApiUrl(`/sales/voids${base}&type=1`);
          break;
        case 'endDay':
          url = buildApiUrl(`/sales/EndDayReport?tenantReference=${ref}&startDate=${endDayDate}&type=1`);
          break;
        default:
          return;
      }

      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();

      if (json.code === 200 && json.data) {
        const d = json.data;
        const items = Array.isArray(d) ? d : (d.list || d.items || d.orders || d.data || []);
        const total = Array.isArray(d) ? d.length : (d.total ?? d.totalOrders ?? items.length);

        setTabs(prev => ({
          ...prev,
          [tabKey]: { items, page, total, loading: false, rawData: d },
        }));
        return d;

      } else {
        setTabs(prev => ({
          ...prev,
          [tabKey]: { items: [], page: 1, total: 0, loading: false, rawData: null },
        }));
        return null;
      }
    } catch (err) {
      console.error(`Failed to fetch ${tabKey}`, err);
      setTabs(prev => ({
        ...prev,
        [tabKey]: { items: [], page: 1, total: 0, loading: false, rawData: null },
      }));
      return null;
    }
  }, [selectedRestaurant, startDate, endDate, endDayDate]);

  // ── 请求 Summary 接口 ─────────────────────────────────────────────────────
  const fetchSummary = useCallback(async () => {
    const ref = selectedRestaurant?.reference;
    if (!ref) return;
    try {
      const url = buildApiUrl(`/sales/summary?tenantReference=${ref}&startDate=${startDate}&endDate=${endDate}`);
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      if (json.code === 200 && json.data) {
        setSummary({
          totalSales: json.data.totalSales ?? 0,
          totalTransactions: json.data.totalTransactions ?? 0,
          averageOrder: json.data.averageOrder ?? 0,
        });
      }
    } catch (err) {
      console.error('Failed to fetch summary', err);
    }
  }, [selectedRestaurant, startDate, endDate]);

  // ── 初始加载 
  // 只加载当前 activeTab
  useEffect(() => {
    if (!selectedRestaurant?.reference) return;
    console.log("activeTable=========>", activeTable);
    setTabs({});
    fetchTab(activeTable, 1);
    fetchSummary();
  }, [selectedRestaurant]);

  // ── 切换 Tab 时按需加载 
  const handleTabChange = (key) => {
    setActiveTable(key);
    if (key === 'endDay') {
      handleEndDay();
    } else {
      fetchTab(key, 1);
    }
  };

  // ── 翻页处理 
  const handlePageChange = (page) => {
    fetchTab(activeTable, page);
  };

  // 只刷新当前 tab
  const handleApplyDate = () => {
    setShowDatePicker(false);
    setTabs({});
    fetchTab(activeTable, 1);
    fetchSummary();
  };

  // ── 导出 
  const handleExport = async () => {
    const ref = selectedRestaurant?.reference;
    if (!ref) return;
    const base = `?tenantReference=${ref}&startDate=${startDate}&endDate=${endDate}`;
    let url, blob;
    const currentConfig = TABLE_CONFIG[activeTable] || TABLE_CONFIG.salesItem;

    if (activeTable === 'salesItem') {
      url = buildApiUrl(`/sales/salesItem${base}&type=2`);
      if (!url) return;
      const res = await fetch(url, { headers: getAuthHeaders() });
      blob = await res.blob();
    } else if (activeTable === 'salesReport') {

      url = buildApiUrl(`/sales/newReport${base}&type=2`);
      if (!url) return;
      const res = await fetch(url, { headers: getAuthHeaders() });
      blob = await res.blob();

    } else if (activeTable === 'reservation') {

      url = buildApiUrl(`/sales/reservation${base}&type=2`);
      if (!url) return;
      const res = await fetch(url, { headers: getAuthHeaders() });
      blob = await res.blob();
    } else if (activeTable === 'voidReport') {
      url = buildApiUrl(`/sales/voids${base}&type=2`);
      if (!url) return;
      const res = await fetch(url, { headers: getAuthHeaders() });
      blob = await res.blob();
    } else if (activeTable === 'transaction') {
      url = buildApiUrl(`/transaction/getInfo${base}&type=2`);
      if (!url) return;
      const res = await fetch(url, { headers: getAuthHeaders() });
      blob = await res.blob();
    } else if (activeTable === 'endDay') {
      url = buildApiUrl(`/sales/EndDayReport?tenantReference=${ref}&startDate=${endDayDate}&type=2`);
      if (!url) return;
      const res = await fetch(url, { headers: getAuthHeaders() });
      blob = await res.blob();
    } else {
      return;
    }
    // 下载
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${currentConfig.title}_${Date.now()}.pdf`;
    a.click();
    URL.revokeObjectURL(a.href);

  };
// ── End Day Report 查询
  const handleEndDay = async () => {
    const ref = selectedRestaurant?.reference;
    if (!ref) return;
    setEndDayReportData(null);
    const data = await fetchTab('endDay', 1);
    setEndDayReportData(data);
  };

  const formatDateRange = () => `${startDate} — ${endDate}`;

  // ── Tab & table 配置 
  const currentConfig = TABLE_CONFIG[activeTable] || TABLE_CONFIG.salesItem;

  return (
    <div className="salespage-container">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item => (
            <li
              key={item}
              className={item === 'Sales' ? 'active' : ''}
              onClick={() => onNavigate && onNavigate(item)}
            >
              <span className="nav-icon">{navIcons[item]}</span> {item}
            </li>
          ))}
        </ul>
      </nav>

      <main className="sales-main">
        {/* Date picker */}
        <div className="sales-header">
          <div className="date-picker-container">
            <button className="date-picker-btn" onClick={() => setShowDatePicker(!showDatePicker)}>
              📅 Date Range: {formatDateRange()}
            </button>
            {showDatePicker && (
              <div className="date-picker-dropdown">
                <div className="date-input-group">
                  <label>Start Date:</label>
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
                </div>
                <div className="date-input-group">
                  <label>End Date:</label>
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
                </div>
                <button className="apply-date-btn" onClick={handleApplyDate}>Apply</button>
              </div>
            )}
          </div>
        </div>

        {/* Summary cards */}
        <div className="sales-cards">
          <div className="sales-card">
            <div className="card-title">Total Sales</div>
            <div className="card-value">${Number(summary.totalSales).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            <div className="card-desc">{formatDateRange()}</div>
          </div>
          <div className="sales-card">
            <div className="card-title">Total Transactions</div>
            <div className="card-value">{summary.totalTransactions.toLocaleString()}</div>
            <div className="card-desc">{formatDateRange()}</div>
          </div>
          <div className="sales-card">
            <div className="card-title">Average Order</div>
            <div className="card-value">${Number(summary.averageOrder).toFixed(2)}</div>
            <div className="card-desc">{formatDateRange()}</div>
          </div>
        </div>

        {/* Table section */}
        <div className="table-section">
          <div className="table-header">
            <h2>{currentConfig.title}</h2>
            <Button type="primary" shape="round" style={{ background: "#232946" }} onClick={handleExport}>
              EXPORT
            </Button>
          </div>

          <ConfigProvider theme={theme}>
            <Tabs
              activeKey={activeTable}
              type="card"
              items={TAB_ITEMS}
              onChange={handleTabChange}
              style={{ '--ant-tabs-card-active-bg': '#000000' }}
            />
          </ConfigProvider>


{/* 不使用组件 单独区分 */}
          {activeTable === 'endDay' ? (
            <div className="endday-report">
              <div className="endday-header">
                <span className="endday-header-title">
                  Sales Date Time
                </span>
                <div className="endday-header-actions">
                  <input type="date" value={endDayDate} max={today} onChange={e => setEndDayDate(e.target.value)} />
                  <Button type="primary" shape="round" style={{ background: '#232946' }} onClick={handleEndDay}>
                    Search
                  </Button>
                </div>
              </div>

              {endDayReportData && (() => {
                const d = endDayReportData;
                const salesDateStr = d.salesDateTime || endDayDate || '';
                const printDateStr = d.printDateTime ? new Date(d.printDateTime).toLocaleString('en-GB', { timeZone: APP_TIMEZONE, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
                const branchName = d.branch || '—';

                const summaryItems = [
                  { label: 'Sales Date', value: salesDateStr },
                  // { label: 'Print Date Time', value: printDateStr },
                  // { label: 'Branch', value: branchName },                  
                  { label: 'Cash Sales', value: fmt(d.cashSales) },
                  { label: 'Visa Sales', value: fmt(d.visaSales) },
                  { label: 'MasterCard Sales', value: fmt(d.masterCardSales) },
                  { label: 'JCB Sales', value: fmt(d.jcbSales) },
                  { label: 'Voucher Sales', value: fmt(d.voucherSales) },
                  { label: 'Discount', value: fmt(d.discount) },
                  { label: 'Grab', value: fmt(d.grabSales) },
                  { label: 'Oddle', value: fmt(d.oddleSales) },
                ];

                const totalItems = [
                  { label: 'Total Sales', value: fmt(d.totalSales), bold: true },
                ];

                const subtotalItems = [
                  { label: 'Subtotal Take Away', value: fmt(d.subtotalTakeAway) },
                  { label: 'Subtotal Dine In', value: fmt(d.subtotalDineIn) },
                  { label: 'Service Charges', value: fmt(d.serviceCharges) },
                  { label: 'GST', value: fmt(d.gst) },
                ];

                const renderBreakdown = (title, data) => {
                  if (!data || !Array.isArray(data) || data.length === 0) return null;
                  return (
                    <div className="endday-section">
                      <h3>{title}</h3>
                      <table className="endday-breakdown-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th style={{ textAlign: 'right' }}>Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {data.map((item, i) => (
                            <tr key={i}>
                              <td>{item.productName || item.product || item.name || '—'}</td>
                              <td style={{ textAlign: 'right' }}>{item.qty || item.quantity || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                };

                return (
                  <>
                    <div className="endday-section">
                      <h2>In Shop</h2>
                      {summaryItems.map(({ label, value }) => (
                        <div key={label} className="endday-row">
                          <span className="endday-label">{label}</span>
                          <span className="endday-value">{value}</span>
                        </div>
                      ))}
                    </div>

                    <div className="endday-divider" />

                    <div className="endday-section">
                      {totalItems.map(({ label, value, bold }) => (
                        <div key={label} className={`endday-row${bold ? ' endday-row--bold' : ''}`}>
                          <span className="endday-label">{label}</span>
                          <span className="endday-value">{value}</span>
                        </div>
                      ))}
                    </div>

                    <div className="endday-divider" />

                    <div className="endday-section">
                      {/* <h3>Subtotals</h3> */}
                      {subtotalItems.map(({ label, value }) => (
                        <div key={label} className="endday-row">
                          <span className="endday-label">{label}</span>
                          <span className="endday-value">{value}</span>
                        </div>
                      ))}
                    </div>

                    <div className="endday-divider" />

                    {d.breakdownList && Array.isArray(d.breakdownList) && (() => {
                      const grouped = {};
                      d.breakdownList.forEach(item => {
                        const cat = item.catName || 'Other';
                        if (!grouped[cat]) grouped[cat] = [];
                        grouped[cat].push(item);
                      });
                      return Object.entries(grouped).map(([catName, items]) =>
                        renderBreakdown(`Breakdown - ${catName}`, items)
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          ) : (
            <>
              <Table
                columns={currentConfig.columns}
                dataSource={currentTab.items.map((item, i) => ({
                  ...item,
                  _key: item.id || item.orderId || item.reference || i,
                }))}
                loading={currentTab.loading}
                rowKey="_key"
                pagination={false}
                size="middle"
                tableLayout="fixed"
                scroll={{ x: 1200 }}
                expandable={activeTable === 'transaction' ? {
                  expandedRowRender: record => (
                    <Table
                      columns={transactionSubCols}
                      dataSource={(record.transactions || []).map((tx, i) => ({ ...tx, _key: tx.id || i }))}
                      rowKey="_key"
                      pagination={false}
                      size="small"
                    />
                  ),
                  rowExpandable: record => record.transactions && record.transactions.length > 0,
                } : undefined}
              />

              <div className="pagination-wrapper">
                <Pagination
                  current={currentTab.page}
                  pageSize={PAGE_SIZE}
                  total={currentTab.total}
                  showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                />
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default SalesPage;
